import { ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "./table/StyledTable";

interface SettingsSectionProps {
  title: string;
  description?: string;
  /** Column headings; the trailing actions column is added automatically. */
  columns: string[];
  /** Shows an add button in the section header when provided. */
  onAdd?: () => void;
  addLabel?: string;
  /** Message shown when `children` renders no rows. */
  emptyMessage?: string;
  /** True when there is nothing to list. */
  isEmpty?: boolean;
  children: ReactNode;
}

/**
 * Bordered panel wrapping a settings list: heading, optional add
 * action, table and empty state. Keeps the settings screens on the
 * same rhythm as the case tables.
 */
export const SettingsSection = ({
  title,
  description,
  columns,
  onAdd,
  addLabel = "Hinzufügen",
  emptyMessage = "Noch keine Einträge vorhanden.",
  isEmpty = false,
  children,
}: SettingsSectionProps) => (
  <Box
    sx={{
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 3,
      backgroundColor: "background.paper",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {onAdd && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
          {addLabel}
        </Button>
      )}
    </Box>

    <TableContainer sx={{ border: 0, borderRadius: 0 }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {isEmpty ? (
            <EmptyState message={emptyMessage} colSpan={columns.length + 1} />
          ) : (
            children
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default SettingsSection;
